import { createFileRoute } from "@tanstack/react-router";
import { GonzalAdsPage } from "@/components/calv/GonzalAdsPage";
import { jsonLdScript, breadcrumbSchema, pageHead } from "@/lib/seo";
import { SITE_URL } from "@/lib/locale";

export const Route = createFileRoute("/leistungen/gonzalads")({
  head: () => {
    const h = pageHead("gonzalads", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
            jsonLdScript(
              breadcrumbSchema([
                { name: "Start", path: "/" },
                { name: "Leistungen", path: "/leistungen" },
                { name: "Tools", path: "/leistungen/tools" },
                { name: "GonzalAds", path: "/leistungen/gonzalads" },
              ]),
            ),
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "GonzalAds",
              alternateName: "GonzalAds — Speedy Ad Management",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              description:
                "GonzalAds ersetzt den manuellen Alltag im Meta Business Manager: KI-gestützte Kampagnen-Erstellung, Bulk Engine, Asset-System mit Format-Varianten, Preset-Logik und Automation Rules 24/7.",
              url: `${SITE_URL}/leistungen/gonzalads`,
              sameAs: ["https://gonzalads.com"],
              provider: { "@type": "Organization", name: "Calvarius GmbH", url: SITE_URL },
              offers: {
                "@type": "Offer",
                availability: "https://schema.org/InStock",
                description: "7 Tage kostenlos testen, keine Kreditkarte",
              },
            }),
          ],
    };
  },
  component: () => (
    <GonzalAdsPage
      lang="de"
      eyebrow="Tool · Meta-Kampagnen-Steuerung"
      headline={<>Meta Ads. <em>80% schneller. Endlich unter Kontrolle.</em></>}
      subline="GonzalAds ersetzt den manuellen Alltag im Meta Business Manager durch ein durchdachtes System: KI-gestützte Kampagnen-Erstellung, regelbasierte Bulk-Operationen, kristallklare Creative-Analytics, Performance-Automation rund um die Uhr — alles an einem Ort."
      metaStrip={["EIGENE MARKE", "EIGENE DOMAIN: GONZALADS.COM", "7 TAGE KOSTENLOS"]}
      breadcrumbs={[
        { label: "Start", to: "/" },
        { label: "Leistungen", to: "/leistungen" },
        { label: "Tools", to: "/leistungen/tools" },
        { label: "GonzalAds" },
      ]}
      standpunktKicker="Standpunkt"
      standpunktHeadline={<>Der Business Manager <em>skaliert nicht.</em></>}
      standpunktParagraphs={[
        "Der Meta Business Manager ist eine ausgereifte Werbe-Plattform — aber er ist für Einzel-Konten optimiert, nicht für skalierte Performance-Realitäten. Bei wachsendem Kampagnen-Volumen wird das spürbar. Setup-Zeiten verlängern sich. Wiederholungsarbeit dominiert das Tagesgeschäft. Placement-Fehler entstehen durch manuelles Format-Handling. Ein zentrales Workflow-Tool für Teams fehlt vollständig.",
        "GonzalAds wurde gebaut, um genau diese Engpässe aufzulösen. Fünf Säulen tragen das System: ein KI-gestützter Campaign Wizard, der Kampagnen in rund zehn Minuten produktionsreif macht. Eine Bulk Engine, die aus einer Regel hunderte Ads erzeugt — mit korrekten Placements und Naming-Konventionen. Ein Asset-System mit automatischen Format-Varianten und Creative-Fingerprinting. Preset-Logik für wiederverwendbare Settings. Und Automation Rules, die Kampagnen 24/7 auf KPIs wie ROAS oder CPA steuern.",
        "GonzalAds ist eine Eigenentwicklung von Calvarius — entstanden aus den Anforderungen unserer Performance-Mandate, weiterentwickelt zur eigenständigen Marke mit eigener Plattform. Die Web-App ist über gonzalads.com sieben Tage kostenlos testbar, ohne Kreditkarte. Im Calvarius-Mandate-Kontext setzen wir GonzalAds als Bestandteil der Kampagnen-Steuerung ein und übernehmen Setup, Onboarding und Integration in bestehende Tracking- und Creative-Workflows.",
      ]}
      pillarsKicker="Funktionsumfang"
      pillarsHeadline={<>Die fünf <em>Säulen.</em></>}
      pillars={[
        { num: "01", name: "Campaign Wizard", desc: "Geführte Kampagnen-Erstellung mit KI-Texten. Von der Idee zur live geschalteten Kampagne in rund 10 Minuten." },
        { num: "02", name: "Bulk Engine", desc: "Regelbasierte Kombination von Bildern, Texten und Targetings. 100+ Ads aus einer Regel — mit korrekten Placements und Naming." },
        { num: "03", name: "Asset System", desc: "Bilder, Videos, Texte zentral verwaltet. Automatische Format-Varianten (1:1, 9:16, 16:9). Creative-Fingerprinting verhindert Duplikate." },
        { num: "04", name: "Preset-Logik", desc: "Targeting, Kampagnen-Settings und Creative-Templates einmal definieren — und unbegrenzt wiederverwenden." },
        { num: "05", name: "Automatisierung", desc: "Performance-Regeln auf CPA, ROAS, Spend, CTR. Reaktionszeit unter einer Minute. Pausiert, aktiviert, skaliert — auch nachts." },
      ]}
      uspsKicker="Differenzierung"
      uspsHeadline={<>Was GonzalAds <em>besonders macht.</em></>}
      usps={[
        { num: "01", name: "Niemals falsche Formate", detail: "Placement Safety Guard validiert jede Kampagne vor dem Upload. Stories bekommen 9:16, Feed bekommt 1:1 — automatisch." },
        { num: "02", name: "80–90% Zeitersparnis", detail: "Kampagnen entstehen in Minuten statt Stunden. Wizard plus Bulk Engine ersetzen hunderte manuelle Klicks im Business Manager." },
        { num: "03", name: "Format-Intelligenz", detail: "Pro Motiv werden drei Format-Varianten gepflegt. Meta erhält je Placement automatisch die passende Datei." },
        { num: "04", name: "Creative Fingerprinting", detail: "Jedes Asset bekommt einen eindeutigen Fingerprint. Kein doppelter Upload, klare Performance-Zuordnung." },
        { num: "05", name: "KI-Texte ohne CTAs", detail: "AI generiert Headlines und Primary Texts, die zu Meta-Policies passen — keine direkten Verkaufs-CTAs in der Headline." },
        { num: "06", name: "Team-fähig von Anfang an", detail: "Rollen, Rechte, geteilte Ad Accounts und Agentur-Token-Hierarchie für saubere Zusammenarbeit." },
        { num: "07", name: "Auto-Sync Analytics", detail: "Daten werden im Hintergrund vorgewärmt — beim Login sind die Reports sofort da." },
        { num: "08", name: "Automation Rules 24/7", detail: "Regeln auf CPA, ROAS, Spend, CTR. Reaktionszeit unter einer Minute, kontinuierlich." },
      ]}
      compareKicker="Im direkten Vergleich"
      compareHeadline={<>Business Manager <em>vs. GonzalAds.</em></>}
      compareLeftTitle="Business Manager"
      compareRightTitle="GonzalAds"
      compareRows={[
        { left: "Stunden für einen Launch", right: "Kampagnen in Minuten live" },
        { left: "Hunderte Klicks pro Kampagne", right: "5 Klicks bis zur fertigen Kampagne" },
        { left: "Placement-Fehler durch manuelles Setup", right: "Keine falschen Formate mehr" },
        { left: "Kein zentrales Team-Workflow-Tool", right: "Team-Rollen und Rechteverwaltung" },
      ]}
      valueKicker="Mehrwert"
      valueHeadline={<>Was GonzalAds <em>bewirkt.</em></>}
      values={[
        { strong: "80–90% Zeitersparnis pro Kampagne.", rest: "Was im Meta Business Manager Stunden braucht, läuft im GonzalAds-Wizard in Minuten." },
        { strong: "Null Placement-Fehler.", rest: "Die Validation vor jedem Upload eliminiert eine der häufigsten Fehlerquellen im manuellen Kampagnen-Setup." },
        { strong: "Skalierung ohne Qualitätsverlust.", rest: "Multi-Account, Multi-Mandant, Team-Rollen — Performance-Marketing-Setups, die mit dem nativen Manager nicht mehr handhabbar sind, bleiben mit GonzalAds übersichtlich." },
        { strong: "Performance-Automation rund um die Uhr.", rest: "Regeln greifen unter einer Minute, kontinuierlich. Kampagnen reagieren auf KPI-Änderungen, ohne dass jemand wartet, bis ein Mensch nachschaut." },
        { strong: "Sofort verfügbare Analytics.", rest: "Daten werden im Hintergrund vorgewärmt — Reports sind beim Login da, nicht erst nach dem Laden mehrerer Dashboards." },
      ]}
      audienceKicker="Zielgruppe"
      audienceHeadline={<>Für wen GonzalAds <em>gebaut ist.</em></>}
      audience={[
        { strong: "In-House Performance-Teams mit Meta-Schwerpunkt", rest: "wo der tägliche Aufwand im Business Manager erkennbar zur Bremse wird" },
        { strong: "Werbeagenturen mit mehreren Mandaten", rest: "Setup-Beschleunigung, Multi-Account-Übersicht und Team-Rollen sind hier der größte Hebel" },
        { strong: "Performance-Marketer, die zu viel Zeit im Business Manager verlieren", rest: "und einen schnelleren, präziseren Workflow brauchen" },
        { strong: "Mandate mit kontinuierlichem Test- und Skalierungs-Bedarf", rest: "wo das native Interface ab einem bestimmten Volumen einfach nicht mehr mithält" },
      ]}
      calvariusKicker="Herkunft"
      calvariusHeadline={<>Hinter GonzalAds steht <em>Calvarius.</em></>}
      calvariusBody="GonzalAds ist eine Eigenentwicklung der Calvarius GmbH — entstanden aus den Anforderungen unserer Performance-Marketing-Mandate seit 2013. Wir setzen GonzalAds täglich selbst ein, entwickeln es kontinuierlich weiter und betreuen es als eigenständige Marke unter gonzalads.com."
      ctaKicker="Zwei Pfade"
      ctaHeadline={<>Selbst testen oder <em>im Mandate einsetzen.</em></>}
      cardA={{
        eyebrow: "Self-Service",
        headline: "GonzalAds 7 Tage kostenlos testen",
        body: "Vollständiger Funktionsumfang. Keine Kreditkarte. Direkt auf gonzalads.com.",
        button: "Zur Produktseite",
      }}
      cardB={{
        eyebrow: "Mandate-Integration",
        headline: "GonzalAds als Teil Ihrer Performance-Mandate",
        body: "Wir setzen GonzalAds als Bestandteil der Kampagnen-Steuerung ein und übernehmen Setup, Onboarding und Integration in bestehende Tracking- und Creative-Workflows.",
        button: "Mit unserem Team sprechen",
        to: "/kontakt",
      }}
    />
  ),
});
